import { useEffect, useState } from 'preact/hooks';
import type { PageProps } from '../app';
import { fromBase64, fromHex } from '../core/bytes/encoding';
import { verifyInclusion, type InclusionResult } from '../core/merkle/proof';
import type { InclusionProof as Proof, LogClient } from '../core/log/client';
import { navigate } from '../router';
import { ErrorBox } from '../ui/components/ErrorBox';
import { Hash } from '../ui/components/Hash';
import { Icon } from '../ui/components/Icon';
import { LogLayout } from '../ui/components/LogLayout';
import { InclusionPath } from '../ui/components/MerklePath';
import { Card, Section } from '../ui/components/Section';
import { fmtInt, parseIndex } from '../ui/format';
import { useLogClient } from '../ui/hooks/useLogClient';
import { resolveRoot, type ResolvedRoot } from '../ui/roots';
import { refreshTreeHead, treeHeads } from '../ui/state/treeHeads';
import { LoadingPage, NotFoundLog } from './Log';
import { usePageState } from '../ui/state/pageState';
import { logPath, logRoute } from '../ui/nav';
import { ReturnStrip } from '../ui/components/ReturnStrip';
import { Segmented } from '../ui/components/Segmented';
import { ExportMenu } from '../ui/components/Export';

interface Outcome {
  proof: Proof;
  result: InclusionResult;
  expected?: ResolvedRoot;
  leafHash: Uint8Array;
}

export function InclusionProofPage({ params, query }: PageProps) {
  const { log, client, loading } = useLogClient(params);
  if (loading) return <LoadingPage />;
  if (!log) return <NotFoundLog id={`${params.op}/${params.log}`} />;
  return (
    <LogLayout log={log} {...(client ? { client } : {})} tab="inclusion" trail={[{ label: 'Inclusion proof' }]}>
      <ReturnStrip query={query} />
      {client && <InclusionForm client={client} query={query} />}
    </LogLayout>
  );
}

function InclusionForm({ client, query }: { client: LogClient; query: URLSearchParams }) {
  const id = client.log.id;
  const head = treeHeads.value[id];
  const [autoRan, setAutoRan] = useState(false);
  // Remembered per log; explicit query parameters (deep links) win over what was there before.
  const [mode, setMode] = usePageState<'index' | 'hash'>(`inclusion:${id}:mode`, query.get('hash') ? 'hash' : 'index');
  const [index, setIndex] = usePageState(`inclusion:${id}:index`, query.get('index') ?? '');
  const [hash, setHash] = usePageState(`inclusion:${id}:hash`, query.get('hash') ?? '');
  const [size, setSize] = usePageState(`inclusion:${id}:size`, query.get('size') ?? '');
  const [root, setRoot] = usePageState(`inclusion:${id}:root`, '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = usePageState<unknown>(`inclusion:${id}:err`, undefined);
  const [out, setOut] = usePageState<Outcome | undefined>(`inclusion:${id}:out`, undefined);
  useEffect(() => {
    // Arriving with a different deep link than the remembered one: take the link's values.
    const qi = query.get('index'), qh = query.get('hash'), qs = query.get('size');
    if ((qi !== null && qi !== index) || (qh !== null && qh !== hash)) {
      setMode(qh ? 'hash' : 'index'); setIndex(qi ?? ''); setHash(qh ?? ''); setSize(qs ?? ''); setOut(undefined); setErr(undefined); setAutoRan(false);
    }
  }, [query.get('index'), query.get('hash')]);

  useEffect(() => {
    void refreshTreeHead(client).catch(() => undefined);
  }, [client]);
  useEffect(() => {
    if (head && !size) setSize(String(head.treeSize));
  }, [head]);

  const parsedSize = parseIndex(size);
  const parsedIndex = parseIndex(index);
  const hashBytes = (() => {
    const t = hash.trim();
    try {
      if (/^[0-9a-fA-F]{64}$/.test(t)) return fromHex(t);
      if (t.length >= 43) return fromBase64(t);
    } catch { /* invalid */ }
    return undefined;
  })();
  const canRun = parsedSize !== undefined && parsedSize > 0 && (mode === 'index' ? parsedIndex !== undefined && parsedIndex < parsedSize : hashBytes?.length === 32);

  const run = async (e?: Event) => {
    e?.preventDefault();
    if (!canRun || parsedSize === undefined) return;
    setBusy(true);
    setErr(undefined);
    setOut(undefined);
    navigate(logRoute(client.log, 'inclusion'), { ...(mode === 'index' ? { index: parsedIndex, size: parsedSize } : { hash: hash.trim(), size: parsedSize }), from: query.get('from') }, true);
    try {
      const target = mode === 'index' ? { index: parsedIndex! } : { leafHash: hashBytes! };
      const proof = await client.getInclusionProof(target, parsedSize);
      const leafHash = proof.leafHash ?? hashBytes!;
      const expected = await resolveRoot(client, parsedSize, head, root);
      const result = await verifyInclusion(leafHash, proof.leafIndex, parsedSize, proof.auditPath, expected?.root);
      setOut({ proof, result, leafHash, ...(expected ? { expected } : {}) });
    } catch (ex) {
      setErr(ex);
    } finally {
      setBusy(false);
    }
  };
  // Deep links (e.g. "Verify inclusion" on an entry page) run immediately once the head is known.
  useEffect(() => {
    if (autoRan || !head || !(query.get('index') || query.get('hash')) || (!size && !query.get('size'))) return;
    setAutoRan(true);
    void run();
  }, [head, size]);

  return (
    <div class="stack">
      <Card title={<><Icon name="shield" /> Inclusion proof</>} foot={<>Asks the log for the audit path of an entry and recomputes the Merkle root from the leaf hash. If it equals the signed tree head's root, the entry is provably in the log.{client.kind === 'tiled' && <> This tiled log has no lookup by hash, so the audit path is <em>computed here</em> from the log's hash tiles.</>}</>}>
        <form onSubmit={run}>
          <div class="form-grid">
            <div class="field span2">
              <span>Find the entry by</span>
              <Segmented
                name="mode"
                value={mode}
                onChange={setMode}
                label="Find the entry by"
                options={[
                  { value: 'index', label: 'Entry index', icon: 'list', title: 'The position of the entry in the log' },
                  { value: 'hash', label: 'Leaf hash', icon: 'search', disabled: !client.supportsHashLookup, title: client.supportsHashLookup ? 'SHA-256 of the Merkle leaf (get-proof-by-hash)' : 'Not available for tiled logs — use the entry index' },
                ]}
              />
            </div>
            {mode === 'index' ? (
              <label class="field">
                <span>Entry index</span>
                <input class="input mono" inputMode="numeric" value={index} onInput={(e) => setIndex((e.target as HTMLInputElement).value)} placeholder="e.g. 12345" autofocus />
              </label>
            ) : (
              <label class="field">
                <span>Leaf hash <span class="hint">hex or base64 — SHA-256(0x00 ‖ MerkleTreeLeaf)</span></span>
                <input class="input mono" value={hash} onInput={(e) => setHash((e.target as HTMLInputElement).value)} placeholder="64 hex characters" autofocus />
              </label>
            )}
            <label class="field">
              <span>Tree size <span class="hint">{head ? `current head: ${fmtInt(head.treeSize)}` : ''}</span></span>
              <input class="input mono" inputMode="numeric" value={size} onInput={(e) => setSize((e.target as HTMLInputElement).value)} />
            </label>
            <label class="field span2">
              <span>Expected root <span class="hint">optional — taken from the signed head, heads seen earlier{client.kind === 'tiled' ? ', or recomputed from tiles' : ''}</span></span>
              <input class="input mono" value={root} onInput={(e) => setRoot((e.target as HTMLInputElement).value)} placeholder="hex or base64" />
            </label>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" disabled={!canRun || busy}><Icon name="play" /> {busy ? 'Verifying…' : 'Fetch & verify'}</button>
            {!canRun && index && mode === 'index' && parsedSize !== undefined && parsedIndex !== undefined && parsedIndex >= parsedSize && <span class="small muted">index must be below the tree size</span>}
          </div>
        </form>
      </Card>

      {err !== undefined && <ErrorBox error={err} title="Could not obtain the proof" />}
      {out && (
        <Card
          title={<>{out.result.ok ? <span class="verdict ok"><Icon name="check" /> Verified</span> : <span class="verdict err"><Icon name="x" /> Failed</span>}</>}
          actions={(() => {
            const json = JSON.stringify({ log: client.log.description, log_id: client.log.logId?.toBase64(), leaf_index: out.proof.leafIndex, tree_size: out.proof.treeSize, leaf_hash: out.leafHash.toBase64(), root_hash: out.result.computedRoot?.toBase64(), audit_path: out.proof.auditPath.map((p) => p.toBase64()), verified: out.result.ok }, null, 2);
            return <ExportMenu label="Proof" icon="shield" formats={[{ label: 'Inclusion proof (JSON)', filename: `inclusion-${out.proof.leafIndex}-${out.proof.treeSize}.json`, mime: 'application/json', text: json }]} />;
          })()}
        >
          {out.result.error && <div class="errorbox" style="margin-bottom:0.8rem">{out.result.error}</div>}
          <dl class="kv">
            <dt>Entry</dt>
            <dd><a href={logPath(client.log, `entry/${out.proof.leafIndex}`)}>#{fmtInt(out.proof.leafIndex)}</a> in a tree of {fmtInt(out.proof.treeSize)} entries</dd>
            <dt>Leaf hash</dt>
            <dd><Hash value={out.leafHash} /></dd>
            <dt>Audit path</dt>
            <dd>{out.proof.auditPath.length} sibling hashes{client.kind === 'tiled' ? ' (computed from tiles)' : ' (from the log)'}</dd>
            <dt>Computed root</dt>
            <dd><Hash value={out.result.computedRoot} /></dd>
            <dt>Compared with</dt>
            <dd>
              {out.expected ? <span class="row"><Hash value={out.expected.root} /><span class="muted small">{out.expected.source}</span></span> : <span class="muted">nothing — no signed root for this tree size is known (the log grew since); re-run with the current size or paste a root</span>}
            </dd>
          </dl>
          <div style="margin-top:1rem">
            <Section title="Hash path" count={out.result.steps.length} open={out.result.steps.length <= 12}>
              <div class="legend"><span><span class="node node-leaf">leaf</span> leaf hash</span><span><span class="node node-acc">acc</span> running hash</span><span><span class="node node-sibling">sib</span> sibling from the proof</span></div>
              <InclusionPath leafHash={out.leafHash} steps={out.result.steps} {...(out.result.computedRoot ? { computedRoot: out.result.computedRoot } : {})} {...(out.expected ? { expectedRoot: out.expected.root } : {})} ok={out.result.ok} />
            </Section>
          </div>
        </Card>
      )}
    </div>
  );
}
